import React from "react";
import BaseModal from "./BaseModal";
import Grid from "@mui/material/Grid";
import TextInput, { TextInputContainer } from "../forms/TextInput";
import MenuItem from "@mui/material/MenuItem";
import SessionTypeSelect from "../forms/SessionTypeSelect";
import FileInput from "../forms/FileInput";
import { Alert, Avatar, Button, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, SvgIcon, Switch, Typography } from '@mui/material';
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetAchievementsAwards } from "../../services/ContentManager";
import AchievementAward from "../../containers/Achievements/AchievementAward";
import ResponsiveCircularLoading from "../ResponsiveCircularLoading";
import GenderSelect from "../GenderSelect";
import { SettingsTooltip } from "../forms/CustomTooltip";
import { Add, EmojiEvents } from "@mui/icons-material";
import { firebaseStorage } from "../../firebase";
import axios from "axios";
import SearchIcons from "../SearchIcon";
import * as mui from '@mui/icons-material';
import styled from "@emotion/styled";
import { value } from "lodash/seq";
import ReactDOMServer from 'react-dom/server'
import { svgToString } from "../../services/helper";
import { saveIconFirebase } from "../../services/FirebaseManager";
import { useAchievementType } from "constants/achievementType";
import generateRepetitionPreview, { applyRepeatTypeSelection, checkForRepeatErrors } from "services/repetitionHelper";
import { RepeatType } from "services/repeatType";
import { DatePicker } from "@mui/x-date-pickers";
import { makeStyles } from "@mui/styles";
import { useRankingFilterNormalizer, useRankingFilterValidator } from "services/RankingFilterHelper";

export const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
    boxSizing: 'content-box',
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(['background-color', 'box-shadow'], {
        duration: theme.transitions.duration.shortest,
    }),
    padding: theme.spacing(2),
    margin: theme.spacing(0.5, 0),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
}));


const useStyles = makeStyles((theme) => ({
    radioLabel: {
        color: theme.palette.primary.light,
        display: 'flex',
        alignContent: "space-between",
    },
    preview: {
        color: theme.palette.primary.light,
        "white-space": "pre-line",
        maxHeight: "15vh",
        overflow: "auto",
        "&::-webkit-scrollbar": {
            width: 5,
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.secondary.main,
            borderRadius: "10px"
        },
    },
    root: {
        '& label': {
            color: theme.palette.primary.light,
            fontStyle: "italic",
            '&.MuiInputLabel-shrink': {
                fontStyle: "normal"
            },
            '&.Mui-focused': {
                color: theme.palette.secondary.main,
            },
            '&:hover': {
                color: theme.palette.secondary.main,
            },
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            '& fieldset': {
                borderColor: theme.palette.primary.light,
            },
            '&:hover fieldset': {
                borderColor: theme.palette.secondary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.secondary.main,
                borderWidth: "3px"
            },
        },
        "& .Mui-error": {
            color: theme.palette.error.light,
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.error.light,
            }
        },
    },
    input: {
        color: theme.palette.primary.light,
        '&:focused': {
            borderColor: theme.palette.secondary.main,
        },
        '&:hover': {
            color: theme.palette.secondary.main,
        },
        '&::placeholder': { /* Chrome, Firefox, Opera, Safari 10.1+ */
            color: theme.palette.secondary.main,
            opacity: 1 /* Firefox */
        },
        '&:-ms-input-placeholder': { /* Internet Explorer 10-11 */
            color: theme.palette.secondary.main,
        },
        '&::-ms-input-placeholder': { /* Microsoft Edge */
            color: theme.palette.secondary.main,
        }
    }
}));

const buildConvertedValues = (values) => {
  let converted = { 
    ...values,
    startDate: Date.parse(values.startDate),
    endDate: Date.parse(values.endDate),
  };

  if (converted.repeatType === RepeatType.MONTH) {
    const startMonthJs = dayjs(converted.startMonth).utc(false);
    converted.startDate = startMonthJs.startOf("month").valueOf();
    converted.endDate = startMonthJs.endOf("month").valueOf();
    delete converted.startMonth;
  }

  return converted;
};

const validateAchievementFields = (values) => {
    if (!values.name) return { field: "name", error: "mandatoryField" };
    if (!values.duration) return { field: "duration", error: 'mandatoryField' };
    if (values.duration < 0) return { field: "duration", error: 'invalidValue' };
    if(!values.target) return {field: "target", error: "mandatoryField"};
    if(values.target<0) return {field: "target", error: "invalidValue"};
    if (values.value == null) return { field: "value", error: "mandatoryField" };
return null;
};

/**
 *
 * @param open
 * @param onClose
 * @param onSubmit
 * @param usefulValues
 * @param achievementsFilter
 * @param organizationId
 * @param infoLogoDimension
 * @param defaultValues se è impostato allora è una clone
 * @param cloneAward clona l'award
 * @returns {JSX.Element}
 * @constructor
 */
export default function CreateAchievementsModal({
    open,
    onClose,
    onSubmit,
    usefulValues,
    achievementsFilter,
    organizationId,
    infoLogoDimension,
    defaultValues,
    cloneAward, 
    customField
}) {

    const classes = useStyles();
    const { t } = useTranslation();
    let [values, setValues] = React.useState({});
    let [error, setError] = React.useState({});
    const achievementType = useAchievementType();
    let { awards = [], status } = useGetAchievementsAwards(defaultValues.id, cloneAward);
    const [addIcon, setAddIcon] = React.useState(!!defaultValues.icon)
    const achievementPreview = generateRepetitionPreview(t, values);
    const rankingFilterValidatorFunction = useRankingFilterValidator();
    const rankingFilterNormalizerFunction = useRankingFilterNormalizer();

    React.useEffect(() => {
        let newValues = { 
            repeatType: RepeatType.NONE,
            ...defaultValues,
         }
        delete newValues.id;
        newValues.startDate = dayjs(defaultValues.startDate).format("YYYY-MM-DD");
        newValues.endDate = dayjs(defaultValues.endDate).format("YYYY-MM-DD");
        setAddIcon(!!newValues.icon)
        setValues(newValues)
    }, [defaultValues])

    const save = async () => {

        let convertedValues = buildConvertedValues(values);
        
        const achievementErr = validateAchievementFields(convertedValues);
        if(achievementErr) {
            setError({ [achievementErr.field]: t(achievementErr.error) });
            return
        }

        const filterErr = rankingFilterValidatorFunction(convertedValues);
        if(filterErr) {
            setError({[filterErr.field]: t(filterErr.error)});
            return
        }

        const repeatErr = checkForRepeatErrors(values);
        if(repeatErr) {
            setError({[repeatErr.field]: t(repeatErr.error)});
            return
        }

        if (convertedValues.filter === '') delete convertedValues.filter

        if (!addIcon) {
            convertedValues.icon = null;
            convertedValues.color = null
        }
        else if (convertedValues.icon) {
            //----Salva su file l'icona se non esiste
            if (!saveIconFirebase(mui, convertedValues.icon)) {
                setError({ icon: t('errorOccurred') });
                return;
            }
        }

        // //clone del logo
        // if (defaultValues.logo) {
        //     //salva sul nuovo percorso
        //     let file = await axios.get(defaultValues.logo, {
        //         responseType: 'blob',
        //       });
        //     console.group();
        //     console.log('1', file.data);
        //     await firebaseStorage.ref('organizations/achievements').child(organizationId).put(file.data)
        //     console.log('Uploaded a blob or file!');
        //     console.groupEnd();
        // }

        convertedValues = rankingFilterNormalizerFunction(convertedValues);

        onSubmit(convertedValues, awards[0] ? { ...awards[0], id: null, achievement: null } : null)//esiste un solo award
        close()
    }

    const close = () => {
        setValues({})
        setError({});
        onClose()
    }

    const onTextChange = (value, name) => {
        let newValues = { ...values };

        if (name === "filter") {
            delete newValues.filterValue;
        }

        if (name === "repeatType") {
            newValues = applyRepeatTypeSelection(newValues, value);
        } else {
            newValues[name] = value;
        }

        setError({});
        setValues(newValues);
    }

    return <BaseModal open={open} onClose={close} onSave={save}>
        <Grid container spacing={4} style={{ margin: 0, width: "100%", marginBottom: "1rem" }}>

            <TextInputContainer xs={12} label={t('name')} value={values.name}
                type="string" required
                onTextChange={(value) => { onTextChange(value, "name") }}
                error={error.name} />

            <TextInputContainer xs={12} label={t('description')} value={values.description}
                type="string" multiline
                onTextChange={(value) => { onTextChange(value, "description") }}
                error={error.description} />

            <Grid item xs={12} style={{ padding: "16px" }}>
                <FormControl component="fieldset">
                    <FormLabel component="legend" className={classes.radioLabel}
                        sx={{ color: "primary.light", '&.Mui-focused': { color: "primary.light" } }}>
                        {t('repetition')}
                    </FormLabel>
                    <RadioGroup row value={values.repeatType}
                        onChange={(event) => onTextChange(event.target.value, "repeatType")}
                        className={classes.radioLabel}>
                        <FormControlLabel value={RepeatType.NONE} control={<Radio
                            sx={{ color: "primary.light", '&.Mui-checked': { color: "secondary.main" } }} />}
                            label={t('none')} />
                        <FormControlLabel value={RepeatType.MONTH} control={<Radio
                            sx={{ color: "primary.light", '&.Mui-checked': { color: "secondary.main" } }} />}
                            label={t('monthly')} />
                        <FormControlLabel value={RepeatType.CUSTOM} control={<Radio
                            sx={{ color: "primary.light", '&.Mui-checked': { color: "secondary.main" } }} />}
                            label={t('custom')} />
                    </RadioGroup>
                </FormControl>
            </Grid>

            {
                (String(values.repeatType) === RepeatType.NONE || String(values.repeatType) === RepeatType.CUSTOM) &&
                <TextInputContainer xs={12} required label={t('startDate')} value={values.startDate}
                    type="date"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onTextChange={(value) => {
                        onTextChange(value, "startDate")
                    }}
                    error={error.startDate} />
            }
            {
                (String(values.repeatType) === RepeatType.NONE || String(values.repeatType) === RepeatType.CUSTOM) &&
                <TextInputContainer xs={12} required label={t('endDate')} value={values.endDate}
                    type="date" min={values.startDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onTextChange={(value) => {
                        onTextChange(value, "endDate")
                    }}
                    error={error.endDate} />
            }
            {values.repeatType === RepeatType.MONTH &&
                <DatePicker
                    fullWidth
                    inputVariant="outlined"
                    classes={{ root: classes.root }}
                    inputProps={{ className: classes.input }}
                    views={["year", "month"]}
                    label={t('startMonth')}
                    value={values.startMonth}
                    onChange={(value) => onTextChange(value, "startMonth")}
                    renderInput={(params) => <TextInputContainer xs={12} {...params} helperText={null} />} />
            }
            {
                (String(values.repeatType) === RepeatType.MONTH || String(values.repeatType) === RepeatType.CUSTOM) &&
                <TextInputContainer xs={12} required label={t('repeatNum')} value={values.repeatNum}
                    type="number"
                    min={1} step={1}
                    onTextChange={(value) => {
                        onTextChange(value, "repeatNum")
                    }}
                    error={error.repeatNum} />
            }

            <Grid item xs={12} className={classes.preview}>
                {achievementPreview}
            </Grid>

            <Grid item xs={12} style={{ padding: "16px" }}>
                <SettingsTooltip title={t('daysDurationExplain')}>
                    <div>
                        <TextInput required label={t('daysDuration')} value={values.duration}
                            type="number"
                            min={1} step={1}
                            onTextChange={(value) => { onTextChange(value, "duration") }}
                            error={error.duration} />
                    </div>
                </SettingsTooltip>
            </Grid>

            <TextInputContainer xs={12} required label={t('value')} value={values.value}
                options={usefulValues} select
                onTextChange={(value) => { onTextChange(value, "value") }}
                error={error.value}>
                {usefulValues.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextInputContainer>

            <TextInputContainer xs={12} required label={t('target')} value={values.target}
                type="number"
                min={1} step={1}
                onTextChange={(value) => { onTextChange(value, "target") }}
                error={error.target} />

            <TextInputContainer xs={12} label={t('filterCategory')} value={values.filter}
                options={achievementsFilter} select
                onTextChange={(value) => { onTextChange(value, "filter") }}
                error={error.filter}>
                <MenuItem key='' value=''>
                    <br />
                </MenuItem>
                {achievementsFilter.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextInputContainer>
            {
                values.filter !== null && values.filter === 0 &&
                <SessionTypeSelect xs={12} label={t('filterValue')} value={values.filterValue}
                    onTextChange={(value) => { onTextChange(value, "filterValue") }}
                    error={error.filterValue} />
            }
            {
                values.filter !== null && values.filter === 1 &&
                <>
                    <TextInputContainer xs={6} required label={t('minAge')} value={values.minAge}
                        type="number"
                        min={1} step={1}
                        onTextChange={(value) => { onTextChange(value, "minAge") }}
                        error={error.minAge} />

                    <TextInputContainer xs={6} required label={t('maxAge')} value={values.maxAge}
                        type="number"
                        min={1} step={1}
                        onTextChange={(value) => { onTextChange(value, "maxAge") }}
                        error={error.maxAge} />
                </>
            }
            {
                values.filter !== null && values.filter === 2 &&
                <GenderSelect
                    xs={12} required value={values.filterValue}
                    error={error.filterValue}
                    onTextChange={(value) => { onTextChange(value, "filterValue") }}
                />
            }
            {
                values.filter !== null && values.filter === 3 &&
                <TextInputContainer xs={12} required label={t('filterValue')} value={values.filterValue}
                    options={customField} select
                    onTextChange={(value) => { onTextChange(value, "filterValue") }}
                    error={error.filterValue}>
                    {customField.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextInputContainer>
            }
            {
                values.filter !== null && values.filter === 4 &&
                <>
                    <SessionTypeSelect xs={6} label={t('sessionType')} value={values.minAge}
                        onTextChange={(value) => { onTextChange(value, "minAge") }}
                        error={error.minAge} />
        
                    <TextInputContainer xs={6} required label={t('customField')} value={values.maxAge}
                        options={customField} select
                        onTextChange={(value) => { onTextChange(value, "maxAge") }}
                        error={error.maxAge}>
                        {customField.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                    </TextInputContainer>
        
                </>
            }

            <TextInputContainer xs={12} required label={t('type')} value={values.type}
                options={achievementType} select
                onTextChange={(value) => { onTextChange(value, "type") }}
                error={error.type}>
                {achievementType.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextInputContainer>

            <Grid item xs={12} style={{ padding: "16px" }}>
                {!defaultValues.logo && <Alert severity="info">{infoLogoDimension}</Alert>}
            </Grid>
            <Grid item xs={12} style={{ padding: "16px" }}>
                {
                    defaultValues.logo ?
                        <Avatar src={defaultValues.logo} alt="awardImage" sx={{
                            height: "8rem",
                            width: "8rem",
                            margin: 'auto'
                        }} >
                            <EmojiEvents fontSize={"large"} />
                        </Avatar>
                        :
                        <FileInput folder={"organizations/achievements"} prefix={organizationId}
                            onRequestSave={(e) => setValues({ ...values, "logo": e })} label={t('dragImage')} />
                }
            </Grid>
            <Grid item xs={12} style={{ padding: "16px" }}>
                <FormGroup>
                    <FormControlLabel control={<Switch color="secondary" checked={addIcon} />} label={t('addIcon')} onChange={() => setAddIcon((addIcon) => !addIcon)} />
                </FormGroup>
                {addIcon && (
                    values.icon ?
                        <Grid container
                            sx={{
                                '&>svg.MuiSvgIcon-root': (theme) => ({
                                    color: values.color,
                                    border: error.icon && '2px solid ' + theme.palette.error.light,
                                })
                            }}
                        >
                            <StyledSvgIcon
                                component={mui[values.icon]}
                                fontSize="large"
                                tabIndex={-1}
                            />
                            <Button startIcon={<mui.Edit />} variant='text' color='secondary' onClick={() => setValues({ ...values, "icon": null })}>
                                {t('edit')}
                            </Button>
                            <Grid item xs={12}>
                                <Typography variant={'caption'} sx={{ color: 'error.light' }}>{error.icon}</Typography>
                            </Grid>
                        </Grid>
                        :
                        <SearchIcons selectIcon={(icon, color) => setValues({ ...values, icon: icon.importName, color: color })}></SearchIcons>
                )}
            </Grid>
            <Grid item xs={12} style={{ padding: "16px" }}>
                {
                    status !== 'loading' &&
                    <AchievementAward sx={{ borderRadius: 2, margin: 0 }} achievement={defaultValues}
                        overrideAwards={awards} onEdit={(values) => awards[0] = values} forceEdit />
                }
                {
                    status === 'loading' && <ResponsiveCircularLoading />
                }
            </Grid>
        </Grid>
    </BaseModal>
}